import React, { CSSProperties, useEffect, useState } from 'react';

import {
  Box,
  Button,
  ClickAwayListener,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  Typography,
} from '@mui/material';

import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import { useNavigate } from 'react-router-dom';
import { IBooking } from '@homeporter/types';
import CurrentTime from '../../../icons/CurrentTimeIcon';
import DetailsIcon from '../../../icons/DetailsIcon';
import EmailIcon from '../../../icons/EmailIcon';
import SelectLogo from '../../../icons/field_logo.png';

import PhoneIcon from '../../../icons/PhoneNumberIcon';
import UserIcon from '../../../icons/UserIcon';

import {
  EditCalendar,
  EventBusy,
  LibraryBooks,
  LiveHelpOutlined,
  MoreVert,
} from '@mui/icons-material';
import { useBookingsContext } from '../../../contexts/BookingsContext';
import { BOOKING_STATUS } from '../../../global/constants/status';
import { formatMobileNumber } from '../../../utils/helpers';
import { useSessionSummaryStyles } from './styles';
import dayjs from 'dayjs';
import DetailsItem from '../../../components/DetailsItem';
import { StyledDivider } from '../ScheduleCardDetails/styles';
import Close from '@mui/icons-material/Close';

import { parseISO, addMinutes, format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

interface ISessionSummaryModal {
  details?: IBooking;
  open: boolean;
  onClose: () => void;
}

const SessionSummaryModal = ({
  open,
  onClose,
  details,
}: ISessionSummaryModal) => {
  console.log('::::SessionSummaryModal...');
  const styles = useSessionSummaryStyles();
  const navigate = useNavigate();
  const {
    selectedBooking,
    updateBookingStatus: updateBookingStatus,
    setBookingListType,
    setIsBookingModalOpen,
    setBookingModalUrl,
    groupedBookings,
    getIconStyle,
    contextPresent,
    isDeleteBookingModalOpen,
    setIsDeleteBookingModalOpen,
  } = useBookingsContext();

  const [description, setDescription] = useState('');
  const [yearBuilt, setYearBuilt] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');

  const [connectionTestSent, setConnectionTestSent] = useState(false);
  const [moreMenu, setMoreMenu] = useState(false);

  const pastDate =
    new Date().getTime() > new Date(selectedBooking?.booking_date).getTime();

  const userPastBookings = groupedBookings?.filter(
    (item) =>
      item?.payment?.user?.user_id === selectedBooking?.payment?.user?.user_id,
  );

  useEffect(() => {
    const description = selectedBooking?.description;
    const yearBuiltRegex = /Year Built: (\d{4})/;
    const purchaseDateRegex = /Purchase Date: (\d{4})/;

    const yearBuiltMatch = description?.match(yearBuiltRegex);
    const purchaseDateMatch = description?.match(purchaseDateRegex);

    setYearBuilt(yearBuiltMatch && yearBuiltMatch[1] ? yearBuiltMatch[1] : '');
    setPurchaseDate(
      purchaseDateMatch && purchaseDateMatch[1] ? purchaseDateMatch[1] : '',
    );
    setDescription(
      selectedBooking?.description
        ?.replace(yearBuiltRegex, '')
        .replace(purchaseDateRegex, ''),
    );
    console.log(selectedBooking);
  }, [selectedBooking]);

  const splitDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    const formattedDate = date.toISOString().split('T')[0];
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const formattedTime = `${hours}:${minutes}`;

    setDate(formattedDate);
    setTime(formattedTime);
  };

  const calculateSessionTimeRange = (
    startTime: string,
    durationInMinutes: number,
    timezoneName: string,
  ): string => {
    const startDate = toZonedTime(parseISO(startTime), timezoneName);

    const endDate = toZonedTime(
      addMinutes(parseISO(startTime), durationInMinutes),
      timezoneName,
    );
    /*
    const formattedStartTime = startDate.format('h:mm A');
    const formattedEndTime = endDate.format('h:mm A');

    const timezoneAbbreviation = startDate.format('z');
*/
    const formattedStartTime = format(startDate, 'h:mm a');
    const formattedEndTime = format(endDate, 'h:mm a');

    const timezoneAbbreviation =
      new Intl.DateTimeFormat('en-US', {
        timeZone: timezoneName,
        timeZoneName: 'short',
      })
        .formatToParts(startDate)
        .find((p) => p.type === 'timeZoneName')?.value || '';

    return `${formattedStartTime} - ${formattedEndTime} ${timezoneAbbreviation}`;
  };

  const redirectToVHCPage = async () => {
    navigate(`inspector/${selectedBooking.booking_session_code}`);
  };

  useEffect(() => {
    if (details?.booking_date) {
      splitDateTime(details?.booking_date as string);
    }
  }, [details?.booking_date]);

  const print = () => {
    window.print();
  };

  const extractPromoCode = (inputText: string | undefined): string => {
    const regex = /promo-code-(.*?)\-/;
    if (!inputText) {
      return 'No promo code found';
    }
    const match = inputText.match(regex);
    return match ? match[1] : 'No promo code found';
  };

  const handleComplete = () => {
    onClose();
    try {
      updateBookingStatus?.(BOOKING_STATUS.SESSION_COMPLETED);
      onClose();
    } catch (error) {
      console.log('ERROR', error);
    }
  };

  const handleReschedule = () => {
    onClose();
    console.log(
      `load: ${window._env_?.BOOKING_APP_URL || process.env.REACT_APP_BOOKING_APP_URL}/reschedule/${selectedBooking?.booking_id}/${selectedBooking?.confirmation_code}?headless=1&dashboard=1`,
    );

    setBookingModalUrl(
      `${window._env_?.BOOKING_APP_URL || process.env.REACT_APP_BOOKING_APP_URL}/reschedule/${selectedBooking?.booking_id}/${selectedBooking?.confirmation_code}?headless=1&dashboard=1`,
    );
    setIsBookingModalOpen(true);
  };

  const handleCancel = async () => {
    try {
      await updateBookingStatus?.(BOOKING_STATUS.SESSION_CANCELED);
      onClose();
    } catch (error) {
      console.log('ERROR', error);
    }
  };

  const redirectToReportEditor = async () => {
    onClose();
    console.log('redirect edit-report');
    navigate(`/edit-report/${selectedBooking.booking_id}`);
  };

  const formatDay = (dateString: string): string => {
    const date = new Date(dateString);

    const dayFormatter = new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
    return dayFormatter.format(date);
  };

  const sendConnectionTest = () => {
    setConnectionTestSent(true);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          setMoreMenu(false);
          onClose();
        }}>
        <Box sx={styles.modal}>
          <Box sx={styles.modalContainer}>
            <Box sx={styles.reciptHeader}>
              <Box paddingLeft={'0.4rem'} paddingRight={'0.4rem'}>
                <Box display={'flex'}>
                  <Typography display={'flex'} alignItems={'center'}>
                    <Box
                      sx={{
                        ...getIconStyle(selectedBooking.payment.plan.label),
                        marginRight: '1rem',
                      }}></Box>
                    {selectedBooking.title}
                  </Typography>
                </Box>
              </Box>
              <Box sx={styles.actionButtonCon}>
                {selectedBooking?.status !== BOOKING_STATUS.REPORT_STARTED && (
                  <>
                    <IconButton sx={styles.actionButton}>
                      <EditCalendar
                        onClick={handleReschedule}
                        sx={styles.iconSize}
                      />
                    </IconButton>
                    <IconButton onClick={handleCancel} sx={styles.actionButton}>
                      <EventBusy sx={styles.iconSize} />
                    </IconButton>
                  </>
                )}
                <IconButton
                  onClick={() => setMoreMenu(true)}
                  sx={styles.actionButton}>
                  <MoreVert sx={styles.iconSize} />
                </IconButton>
                <IconButton
                  onClick={() => {
                    onClose();
                    setMoreMenu(false);
                  }}
                  sx={styles.actionButton}>
                  <Close sx={styles.iconSize} />
                </IconButton>
                {moreMenu && (
                  <ClickAwayListener onClickAway={() => setMoreMenu(false)}>
                    <Box
                      sx={{
                        marginTop: '2rem',
                        background: '#fff',
                        boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
                        border: 'solid 1px #e2e2e2',
                      }}
                      position={'absolute'}
                      width={'10rem'}
                      height={'auto'}>
                      {contextPresent && (
                        <>
                          <MenuItem
                            onClick={() => {
                              setMoreMenu(false);
                              redirectToReportEditor();
                            }}>
                            Re-EnterVHC
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              handleComplete();
                              setMoreMenu(false);
                            }}>
                            Mark complete
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              handleReschedule();
                              setMoreMenu(false);
                            }}>
                            Reschedule
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              handleComplete();
                              setMoreMenu(false);
                            }}>
                            Cancel
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              setIsDeleteBookingModalOpen(true);
                              setMoreMenu(false);
                            }}>
                            Delete
                          </MenuItem>
                        </>
                      )}{' '}
                      {selectedBooking?.status === BOOKING_STATUS.OPEN &&
                        selectedBooking?.status !==
                          BOOKING_STATUS.REPORT_STARTED &&
                        selectedBooking?.status !==
                          BOOKING_STATUS.SESSION_COMPLETED &&
                        !contextPresent && (
                          <>
                            <MenuItem
                              onClick={() => {
                                setMoreMenu(false);
                                redirectToReportEditor();
                              }}>
                              Begin the Report
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                handleComplete();
                                setMoreMenu(false);
                              }}>
                              Mark complete
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                setIsDeleteBookingModalOpen(true);
                                setMoreMenu(false);
                              }}>
                              Delete
                            </MenuItem>
                          </>
                        )}
                      {selectedBooking?.status ===
                        BOOKING_STATUS.SESSION_COMPLETED &&
                        contextPresent && (
                          <>
                            <MenuItem
                              onClick={() => {
                                setMoreMenu(false);
                                redirectToReportEditor();
                              }}>
                              Edit Report
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                handleReschedule();
                                setMoreMenu(false);
                              }}>
                              Reschedule
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                handleCancel();
                                setMoreMenu(false);
                              }}>
                              Cancel
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                setIsDeleteBookingModalOpen(true);
                                setMoreMenu(false);
                              }}>
                              Delete
                            </MenuItem>
                          </>
                        )}
                      {selectedBooking?.status ===
                        BOOKING_STATUS.SESSION_CANCELED && (
                        <>
                          <MenuItem
                            onClick={() => {
                              handleReschedule();
                              setMoreMenu(false);
                            }}>
                            Reschedule
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              setIsDeleteBookingModalOpen(true);
                              setMoreMenu(false);
                            }}>
                            Delete
                          </MenuItem>
                        </>
                      )}
                      {selectedBooking?.status ===
                        BOOKING_STATUS.SESSION_COMPLETED &&
                        !contextPresent && (
                          <>
                            <MenuItem
                              onClick={() => {
                                setMoreMenu(false);
                                redirectToReportEditor();
                              }}>
                              Create a Report
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                handleReschedule();
                                setMoreMenu(false);
                              }}>
                              Reschedule
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                handleCancel();
                                setMoreMenu(false);
                              }}>
                              Cancel
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                setIsDeleteBookingModalOpen(true);
                                setMoreMenu(false);
                              }}>
                              Delete
                            </MenuItem>
                          </>
                        )}
                    </Box>
                  </ClickAwayListener>
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={styles.modalContainer}>
            <Typography
              fontWeight={500}
              marginLeft={'2rem'}
              marginRight={'2rem'}>
              {formatDay(selectedBooking.booking_date)}
            </Typography>
            <Typography
              sx={{
                textDecoration:
                  selectedBooking?.status === BOOKING_STATUS.SESSION_CANCELED
                    ? 'line-through'
                    : 'none',
              }}>
              {calculateSessionTimeRange(
                selectedBooking.booking_date,
                selectedBooking.session_duration,
                selectedBooking?.timezone_name,
              )}
            </Typography>
          </Box>
          {selectedBooking?.status === BOOKING_STATUS.SESSION_COMPLETED && (
            <Box>
              <Typography
                fontSize={'0.8rem'}
                paddingTop={'0.5rem'}
                paddingBottom={'0.5rem'}
                paddingLeft={'2.5rem'}>
                Complete
              </Typography>
            </Box>
          )}
          {selectedBooking?.status === BOOKING_STATUS.SESSION_CANCELED && (
            <Box>
              <Typography
                fontSize={'0.8rem'}
                paddingTop={'0.5rem'}
                paddingBottom={'0.5rem'}
                paddingLeft={'2.5rem'}>
                Cancelled
              </Typography>
            </Box>
          )}
          {selectedBooking?.status !== BOOKING_STATUS.SESSION_COMPLETED && (
            <Box sx={styles.modalContainer}>
              {contextPresent && (
                <Button
                  onClick={redirectToReportEditor}
                  sx={styles.enterSessionButton}>
                  Edit Report
                </Button>
              )}
              {selectedBooking?.status !== BOOKING_STATUS.REPORT_STARTED &&
                !contextPresent && (
                  <Button
                    onClick={redirectToVHCPage}
                    sx={styles.enterSessionButton}>
                    Enter VHC
                  </Button>
                )}
              {selectedBooking?.status === BOOKING_STATUS.REPORT_SENT &&
                contextPresent && (
                  <Button
                    onClick={redirectToVHCPage}
                    sx={styles.viewReportButton}>
                    View Report
                  </Button>
                )}
            </Box>
          )}
          {selectedBooking?.status !== BOOKING_STATUS.SESSION_COMPLETED && (
            <Box sx={styles.connectionTest}>
              <Typography
                onClick={() => sendConnectionTest()}
                sx={styles.connectionTestText}>
                <LiveHelpOutlined sx={{ marginRight: '0.5rem' }} /> Send
                connection test
              </Typography>
              <Typography sx={{ fontSize: '0.85rem' }}>
                {selectedBooking?.status !== BOOKING_STATUS.REPORT_SENT &&
                connectionTestSent
                  ? 'Test Sent'
                  : 'Test Incomplete'}
                {selectedBooking?.status === BOOKING_STATUS.REPORT_SENT &&
                  'Report Sent' + 'Date'}
              </Typography>
            </Box>
          )}
          <Box paddingLeft={'1rem'} paddingRight={'1rem'}>
            <Box sx={{ maxHeight: '78vh' }}>
              <Box borderTop={'1px solid lightgray'}>
                <Box sx={styles.detailsHeader}>
                  <Typography style={styles.detailSubHeading}>
                    DETAILS
                  </Typography>

                  <Box
                    sx={{
                      ...styles.information,
                      marginLeft: 'auto',
                      width: '50%',
                    }}>
                    <img
                      width={22}
                      height={'auto'}
                      src={SelectLogo}
                      alt="Select Logo"
                    />
                    <Typography sx={styles.bookingText}>
                      {details?.payment?.plan?.label
                        ? details?.payment?.plan?.label
                        : 'Undefined'}
                    </Typography>
                  </Box>
                </Box>
                <Grid
                  container
                  spacing={2}
                  borderBottom={'1px solid lightgray'}
                  paddingBottom={'1.5rem'}
                  sx={styles.container}>
                  <Grid
                    item
                    xs={5.5}
                    sx={styles.gridItem}
                    paddingTop={'0px !important'}>
                    <Box sx={styles.information}>
                      <UserIcon />
                      <Typography
                        sx={{
                          ...styles.bookingText,
                        }}>
                        {details?.title ? `${details?.title}` : 'Undefined'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={5.5}
                    sx={styles.gridItem}
                    paddingTop={'0px !important'}>
                    <Box sx={styles.information}>
                      <EmailIcon />
                      <Typography sx={styles.bookingText}>
                        {details?.payment?.user.email
                          ? details.payment?.user.email
                          : 'Undefined'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={5.5}
                    sx={styles.gridItem}
                    paddingTop={'0px !important'}>
                    <Box sx={styles.information}>
                      <PhoneIcon />
                      <Typography sx={styles.bookingText}>
                        {details?.mobile_number
                          ? formatMobileNumber(details.mobile_number)
                          : 'Undefined'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={5.5}
                    sx={styles.gridItem}
                    paddingTop={'0px !important'}>
                    {details?.address ? (
                      <>
                        <Box sx={styles.information}>
                          <Box sx={styles.iconSize}>
                            <LocationOnOutlined />
                          </Box>
                          <Typography
                            sx={{
                              ...styles.bookingText,
                              marginBottom: '-0.5rem',
                              textDecoration: 'underline',
                            }}>
                            {details?.address ? details.address : 'Undefined'}
                          </Typography>
                        </Box>
                        <Typography sx={styles.address}></Typography>
                        <Typography sx={styles.address}>
                          {details?.city ? details.city : 'Undefined'},{' '}
                          {details?.province ? details.province : 'Undefined'}
                        </Typography>
                        <Typography sx={styles.address}>
                          {details?.postal_code
                            ? details.postal_code
                            : 'Undefined'}
                        </Typography>
                      </>
                    ) : null}
                  </Grid>

                  <Grid
                    item
                    xs={5.5}
                    sx={styles.gridItem}
                    paddingTop={'0px !important'}>
                    <Box sx={styles.information}></Box>
                  </Grid>
                </Grid>
              </Box>
              <>
                <Box paddingLeft={'0.8rem'} paddingRight={'0.8rem'}>
                  <Typography style={styles.HomeOwnerSubHeading}>
                    HOME DETAILS
                  </Typography>
                  <Grid
                    container
                    spacing={2}
                    borderBottom={'0px !important'}
                    sx={styles.containerBottom}>
                    {yearBuilt ? (
                      <Grid
                        item
                        xs={5.5}
                        sx={styles.gridItem}
                        paddingTop={'0px !important'}
                        marginBottom={'0px'}>
                        <Box sx={styles.homeInfoText}>
                          <Typography sx={styles.sectionTitle}>
                            When was your home built?
                          </Typography>
                          <Typography sx={styles.information}>
                            {yearBuilt ? yearBuilt : 'Not Provided'}
                          </Typography>
                        </Box>
                      </Grid>
                    ) : null}
                    {purchaseDate ? (
                      <Grid
                        item
                        xs={5.5}
                        sx={styles.gridItem}
                        paddingTop={'0px !important'}>
                        <Box sx={styles.homeInfoText}>
                          <Typography sx={styles.sectionTitle}>
                            Purchase Date
                          </Typography>
                          <Typography sx={styles.homeInfoText}>
                            {purchaseDate ? purchaseDate : 'Not Provided'}
                          </Typography>
                        </Box>
                      </Grid>
                    ) : null}
                  </Grid>
                </Box>
                <Box paddingLeft={'0.8rem'} paddingRight={'0.8rem'}>
                  <Grid
                    container
                    spacing={2}
                    borderBottom={'0px solid lightgray !important'}
                    paddingBottom={'1.5rem'}
                    sx={styles.containerDescription}>
                    <Grid
                      item
                      xs={11}
                      padding={'2rem'}
                      display={'in-line flex'}
                      alignItems={'flex-start'}>
                      <DetailsIcon />
                      <Box sx={styles.detailsIcon}>
                        <Typography sx={styles.bookingText}>
                          {description?.length > 4
                            ? description
                            : 'No Description Provided'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </>
            </Box>
            {false && userPastBookings?.length ? (
              <>
                <StyledDivider />

                <Box padding="21px 30px">
                  <Typography sx={styles.subHeading} color="#666e78">
                    PAST SESSION DETAILS
                  </Typography>
                  <Grid
                    container
                    spacing={4}
                    borderBottom={'0px solid lightgray !important'}
                    paddingBottom={'1.5rem'}
                    sx={styles.containerBottom}>
                    {userPastBookings?.map((booking, index) => {
                      return (
                        <>
                          <Box key={index}>
                            <Grid
                              item
                              xs={4}
                              padding={'2rem'}
                              display={'in-line flex'}>
                              <Box sx={styles.detailsIcon}>
                                <img
                                  width={20}
                                  height={20}
                                  src={SelectLogo}
                                  alt="Select Logo"
                                />
                                <Typography sx={styles.bookingText}>
                                  {booking.payment?.plan?.label}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid
                              item
                              xs={4}
                              padding={'2rem'}
                              display={'in-line flex'}>
                              <Box sx={styles.detailsIcon}>
                                <CurrentTime />
                                <Typography sx={styles.bookingText}>
                                  {booking?.booking_date}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid
                              item
                              xs={4}
                              padding={'2rem'}
                              display={'in-line flex'}>
                              <Box sx={styles.detailsIcon}>
                                <LibraryBooks />
                                <Typography
                                  sx={{
                                    ...styles.bookingText,
                                    textDecoration: 'underline',
                                  }}>
                                  {'View Report'}
                                </Typography>
                              </Box>
                            </Grid>
                          </Box>
                        </>
                      );
                    })}
                  </Grid>
                  <Grid container alignSelf="center" m="12px 0 0 20%">
                    {userPastBookings?.map((booking, index) => {
                      return (
                        <Grid key={index} item md={11} mb="12px">
                          <Box display="flex">
                            <DetailsItem
                              value={dayjs(booking?.booking_date).format(
                                'MMMM D, YYYY',
                              )}
                              mt="0px"
                            />
                            <DetailsItem
                              key={booking?.booking_id}
                              value={dayjs(booking?.booking_date).format(
                                'HH:mm A EST',
                              )}
                              mt="0px"
                            />
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </>
            ) : null}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default SessionSummaryModal;
